import React, { useState, useRef } from "react";
import { Alert, Card, Overlay, Popover, Button } from "react-bootstrap";
import "!style-loader!css-loader!./styles.css";
import { bool, object, string } from "prop-types";
import greyInfoIcon from "resources/images/info.svg";
import Loader from "react-loader-advanced";
import ThreeBounce from "better-react-spinkit/dist/ThreeBounce";
import { SALES_TAX_VERBIAGE } from "utils/shared";

const OrderSummaryColumn = ({
  allowPaymentPlan,
  callLocation,
  formattedContractPrice,
  formattedFullPrice = null,
  formattedPaymentPlanPrice,
  formattedTotalAmount,
  isAUSubsidiary,
  paymentInformationError = false,
  pricing,
  showLoadingButton = false,
  taxAmountToShow,
  buttonText,
  showOrderDescription,
  showRingFencing,
  showParameter,
  currencyCode,
  platformInstance,
  showAutoRenewalText = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleOnMouseEnter = () => {
    setShowTooltip(true);
  };
  const handleOnMouseLeave = () => {
    setShowTooltip(false);
  };

  const infoIconRef = useRef(null);

  /* ----------- Set UI Variables ------------ */

  const applicableTax =
    SALES_TAX_VERBIAGE[
      isAUSubsidiary || platformInstance === "APAC" ? "AU" : "US"
    ];
  const shouldHideCurrencyCode =
    taxAmountToShow === "Calculated on next screen";

  return (
    <>
      <Card.Header as="h6" className="bg-dark text-white p-0">
        Order Summary
      </Card.Header>
      <Card.Body className="p-0">
        {["zipRecruiterUpgrade", "versionUpgrade"].includes(callLocation) ? (
          <div className="alert alert-info">
            The price you see below is the cost to upgrade for the rest of your
            current subscription term. The full subscription price for your
            subscription next year will be:{" "}
            <strong>{formattedFullPrice}.</strong>
          </div>
        ) : null}
        <table className="order-summary-table table">
          <thead>
            <tr>
              {showOrderDescription ? (
                <td colSpan="12" className="order-description">
                  {pricing.get("descriptions").map((item) => (
                    <div key={item}>
                      <span>
                        {`${item.replace("HireSelect", "Criteria")} -
                       ${
                         pricing.get("months")
                           ? pricing.get("months")
                           : pricing.months
                       } Months`}
                      </span>
                      <br />
                    </div>
                  ))}
                </td>
              ) : null}
            </tr>
            {showRingFencing ? (
              <tr>
                <td>Assessments</td>
                <td>{pricing.get("ringFencing")}</td>
              </tr>
            ) : null}
            {showParameter ? (
              <tr>
                <td>Parameters</td>
                <td>{pricing.get("parameter")}</td>
              </tr>
            ) : null}
          </thead>
          <tbody>
            <tr>
              <td>
                {allowPaymentPlan ? "Full " : null}
                Subscription Price
              </td>
              <td>
                {currencyCode}
                {` `}
                {formattedContractPrice}
              </td>
            </tr>
            {allowPaymentPlan ? (
              <tr>
                <td>Payment Amount</td>
                <td>
                  {currencyCode}
                  {` `}
                  {formattedPaymentPlanPrice}
                </td>
              </tr>
            ) : null}
            <tr>
              <td>
                {applicableTax.label}
                <img
                  id="applicable-tax-info"
                  src={greyInfoIcon}
                  width="16px"
                  height="16px"
                  alt="info"
                  className="ml-1"
                  ref={infoIconRef}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
                <Overlay
                  show={showTooltip}
                  placement="top"
                  target={infoIconRef.current}
                >
                  <Popover
                    id="applicable-tax-popover"
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  >
                    <div
                      className="small-text popover-content"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: applicableTax.definition,
                      }}
                    />
                  </Popover>
                </Overlay>
              </td>
              <td
                style={{
                  color: shouldHideCurrencyCode ? "#53565A" : undefined,
                }}
              >
                {shouldHideCurrencyCode ? "" : `${currencyCode} `}
                {taxAmountToShow}
              </td>
            </tr>
            <tr className="total-price">
              <td>Total</td>
              <td>
                {currencyCode}
                {` `}
                {formattedTotalAmount}
              </td>
            </tr>
          </tbody>
        </table>{" "}
        {showAutoRenewalText ? (
          <p className="mt-3 mb-4">
            Your subscription renews annually unless canceled before the renewal
            date. You can cancel in your Account Billing Details.
          </p>
        ) : null}
        <div className="text-center">
          <Button
            variant="primary"
            size="lg"
            id={buttonText}
            disabled={showLoadingButton}
            type="submit"
          >
            {showLoadingButton ? (
              <Loader
                show
                message={
                  <span>
                    <span className="mr-2">Please wait</span>
                    <ThreeBounce color="#1F469A" size={8} />
                  </span>
                }
                contentBlur={30}
                backgroundStyle={{
                  backgroundColor: "rgba( 0 0 0 0)",
                  position: "relative",
                }}
              />
            ) : (
              buttonText
            )}
          </Button>
        </div>
        {paymentInformationError ? (
          <Alert variant="danger" className="mt-3">
            There was a problem with your payment information. Please verify it
            and try again. If the issue persists, please contact your Account
            Manager.
          </Alert>
        ) : null}
      </Card.Body>
    </>
  );
};

OrderSummaryColumn.propTypes = {
  allowPaymentPlan: bool,
  buttonText: string,
  callLocation: string,
  formattedContractPrice: string,
  formattedFullPrice: string,
  formattedPaymentPlanPrice: string,
  formattedTotalAmount: string,
  isAUSubsidiary: bool,
  paymentInformationError: bool,
  pricing: object,
  showLoadingButton: bool,
  taxAmountToShow: string,
  showOrderDescription: bool,
  showRingFencing: bool,
  showParameter: bool,
  currencyCode: string,
  platformInstance: string,
  showAutoRenewalText: bool,
};

export default OrderSummaryColumn;
